/**
 * Revoked Channel Targets Policy (TV1-2446 slice 6, design D4 — UI half)
 *
 * When a process is loaded (or the page switches to another process), reads the credential
 * state of each channel integration the batch targets and records the revoked ones, so Retry
 * is disabled with a Reconnect call to action instead of failing on the server's
 * `CHANNEL_CREDENTIAL_REVOKED` refusal.
 *
 * FAIL-OPEN like the server: an integration whose read fails is not recorded as revoked. Read
 * once per process, not on every progress tick; a credential restored while the page is open
 * shows up on the next load (the server refusal stays authoritative meanwhile).
 *
 * The detail store is one module-level store that outlives in-app navigation between processes,
 * so on every process change a non-empty list is first RESET to empty — and a batch with no
 * channel targets always gets the empty list, with no read — and a read that resolves after the
 * page moved on to another process is dropped rather than written over the new process's state.
 *
 * The target derivation and the fail-open read are the shared ones
 * (`batchProcesses/shared/state/channelTargets.ts`), also used by the list surfaces.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { BatchProcessesDetailState } from '../state';
import { BatchProcessesDetailCommand } from '../commands';
import { batchChannelTargets, readRevokedChannelTargets, ReadIntegrationCredentialState } from '../../../shared/state/channelTargets';

export const createRevokedChannelTargetsPolicy = (
  readIntegration: ReadIntegrationCredentialState
): Policy<BatchProcessesDetailState, BatchProcessesDetailCommand> => ({
  name: 'revoked-channel-targets',

  shouldReact: ( prev, next ) =>
    prev.process?.id !== next.process?.id,

  onStateChange: async ({ next, ctx }) => {
    const processId = next.process?.id;
    const targets = batchChannelTargets( next.process );

    if ( targets.length === 0 || next.revokedChannelTargets.length > 0 ) {
      ctx.dispatch(
        { type: 'batchProcesses/detail/data/setRevokedChannelTargets', payload: { integrationIds: []}},
        { source: 'api' }
      );
    }
    if ( targets.length === 0 ) return;

    const revoked = await readRevokedChannelTargets( targets, readIntegration );
    if ( ctx.getState().process?.id !== processId ) return;

    ctx.dispatch(
      { type: 'batchProcesses/detail/data/setRevokedChannelTargets', payload: { integrationIds: revoked }},
      { source: 'api' }
    );
  }
});
